import { createSelector } from 'reselect';

import { getVariantType } from 'helpers/utils/variantType';
import { ProductState, Variant, SizeData } from './types';
import { RemoteConfig } from 'store/remoteConfig/types';
import { AppState } from 'store/app/types';
import { ADD_TO_BAG_TEXT } from '@nykaa/product-card-shared/constant/addToCart';

const productPage = (state: { productPage: ProductState }) => state.productPage;

export const getSelectedSku = (product: any) => {
  if (product) {
    const { selectedVariantId, variants } = product;
    if (selectedVariantId && variants && variants.length > 0) {
      let selectedVariantIndex = variants.findIndex(
        (variant) => variant.childId === selectedVariantId
      );
      if (selectedVariantIndex < 0) {
        selectedVariantIndex = 0;
      }
      return variants[selectedVariantIndex];
    } else {
      return {
        ...product,
        newButtonText: product.newButtonText || ADD_TO_BAG_TEXT,
        childId: product.id,
        isBestSeller: false,
        media: product?.parentMedia,
        variantName: product.name,
        imageUrl: product?.parentMedia[0]?.url,
        title: product.name,
        earlyAccessData: product?.earlyAccessData || {},
        secondaryTag: product?.secondaryTag,
        secondaryTagV2: product?.secondaryTagV2 || '',
      };
    }
  }
};

export const selectCurrentSku = createSelector(
  [productPage],
  (productData): Variant | undefined => {
    const { product } = productData;
    return getSelectedSku(product);
  }
);

export const selectCurrentProductId = createSelector(
  [productPage],
  (productData): string | undefined => {
    const { product } = productData || {};

    if (product) {
      return product.id;
    }
  }
);

export const selectIsProductLoading = createSelector(
  [productPage],
  (productData) => productData.loading
);

export const selectIsFetchingError = createSelector(
  [productPage],
  (productData) => productData.isFetchingError
);

export const selectRatingInfo = createSelector([productPage], (productData) => {
  const { product } = productData;
  if (product) {
    const { rating, ratingCount, reviewCount, qnaCount, reviewSplitUp } =
      product;

    return {
      rating,
      ratingCount,
      reviewCount,
      qnaCount,
      reviewSplitUp,
    };
  }
});

export const selectReturnDetails = createSelector(
  [productPage],
  (productData) => {
    const { product } = productData;
    if (product) {
      const {
        brandAuth,
        messageOnReturn,
        returnAvailable,
        sellerName,
        primaryCategories,
      } = product;

      return {
        genuineProductText: brandAuth?.text || '',
        messageOnReturn,
        returnAvailable,
        sellerName,
        primaryCategories,
      };
    }
  }
);

export const selectPriceInfo = createSelector([productPage], (productData) => {
  const { product } = productData;
  if (product) {
    const { discount, mrp, offerPrice } = product;

    return {
      discount,
      mrp,
      offerPrice,
    };
  }
});

export const selectOffers = createSelector([productPage], (productData) => {
  const { product } = productData;
  if (product) {
    const { offersData = [] } = product;

    return offersData;
  }
});

const isPdpCustomVideo = (state: {
  remoteConfig: RemoteConfig;
  appReducer: AppState;
}) => state.remoteConfig.switches.dwebPdpCustomVideo || false;

export const selectProductMedia = createSelector(
  [productPage, isPdpCustomVideo],
  (productData, pdpCustomVideoEnabled) => {
    const { product } = productData;
    if (product) {
      const { selectedVariantId, variants } = product;
      if (selectedVariantId && variants && variants.length > 0) {
        let selectedVariantIndex = variants.findIndex(
          (variant) => variant.childId === selectedVariantId
        );
        if (selectedVariantIndex < 0) {
          selectedVariantIndex = 0;
        }

        if (
          pdpCustomVideoEnabled &&
          variants[selectedVariantIndex].carousel?.length > 0
        ) {
          return variants[selectedVariantIndex].carousel;
        }
        return variants[selectedVariantIndex].media;
      } else {
        if (pdpCustomVideoEnabled && product.parentCarousel?.length > 0) {
          return product.parentCarousel;
        }
        return product.parentMedia;
      }
    }
  }
);

export const selectProductRelatedVideo = createSelector(
  [productPage, isPdpCustomVideo],
  (productData) => {
    const { product } = productData;
    if (product) {
      const { selectedVariantId, variants } = product;

      if (selectedVariantId && variants && variants.length > 0) {
        let selectedVariantIndex = variants.findIndex(
          (variant) => variant.childId === selectedVariantId
        );
        if (selectedVariantIndex < 0) {
          selectedVariantIndex = 0;
        }
        return variants[selectedVariantIndex].relatedVideo;
      } else {
        return product.parentRelatedVideo;
      }
    }
  }
);

export const selectVariants = createSelector([productPage], (productData) => {
  const { product } = productData;
  if (product) {
    const { variants } = product;

    if (variants && variants.length > 0) {
      return variants;
    }
  }
});

export const selectVariantType = createSelector(
  [productPage],
  (productData) => {
    const { product } = productData;
    if (product) {
      const { productType, variantType } = product;

      if (productType === 'simple') {
        return;
      }

      if (productType === 'configurable') {
        return getVariantType(variantType);
      }
    }
  }
);

export const selectBrandAuth = createSelector([productPage], (productData) => {
  const { product } = productData;
  if (product) {
    const { brandAuth } = product;

    return brandAuth;
  }
});

export const selectBrandName = createSelector([productPage], (productData) => {
  const { product } = productData;
  if (product) {
    const { brandName } = product;

    return brandName;
  }
});

interface SizeChartOption {
  id: string;
  packSize: string;
  sizeData: SizeData;
  inStock: boolean;
}
export const selectSizeChartVariants = createSelector(
  [productPage],
  (productData) => {
    const { product } = productData;
    const transformedVariants: SizeChartOption[] = [];
    if (product) {
      const { variants } = product;
      if (Array.isArray(variants)) {
        variants?.forEach((variant) => {
          transformedVariants.push({
            packSize: variant?.packSize || '',
            id: variant.childId,
            sizeData: variant.sizeData || {},
            inStock: variant.inStock,
          });
        });
      }
    }
    return transformedVariants;
  }
);

export const selectBundleChildIds = createSelector(
  [productPage],
  (productData) => {
    const { product } = productData;
    const childSkus = product?.bundleChildSkus || {};
    const transformedChildSkus = Object.keys(childSkus) || [];
    return transformedChildSkus;
  }
);
